import type { KemCardProvider } from '../wallet/walletTypes'
import {
  FeaturesSettingsDTO,
  AdaptedFeaturesSettingsDTO,
  FeaturesSettingsAdapterParams,
  FeatureActionType,
} from './settingTypes'

/**
 * Normalize text for case-insensitive comparison
 */
const normalizeText = (text: string): string => {
  return text.toLowerCase().trim()
}

// Helper function to check if action is required
const isActionRequired = (
  feature?: string,
  actionType?: FeatureActionType,
  featureSettings?: FeaturesSettingsDTO,
): boolean => {
  if (!feature || !actionType || !featureSettings) return false

  const featureData =
    featureSettings.features[feature as keyof typeof featureSettings.features]
  if (!featureData || !('actions' in featureData)) return false

  const action = (featureData as { actions: Array<{ data_type: FeatureActionType; required: boolean }> })
    .actions.find((a) => a.data_type === actionType)

  return action?.required ?? false
}

// Helper function to format seconds to duration
const secondsToDurationFormatted = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d`
}

// Helper function to format fiat currency
const formatFiatCurrency = ({
  value,
  fiatCode,
  showDecimals = true,
}: {
  value: number
  fiatCode: string
  showDecimals?: boolean
}): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: fiatCode,
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  })
  return formatter.format(value)
}

export const adapterFeatureSettings = (
  featureSettings: FeaturesSettingsDTO,
  adapterParams?: FeaturesSettingsAdapterParams,
): AdaptedFeaturesSettingsDTO => {
  const normalizedCountry = normalizeText(adapterParams?.documentCountry || '')

  const isKycRequired = isActionRequired(
    adapterParams?.feature,
    'kyc',
    featureSettings,
  )
  const isEmailRequired = isActionRequired(
    adapterParams?.feature,
    'email',
    featureSettings,
  )
  const isPhoneRequired = isActionRequired(
    adapterParams?.feature,
    'phone',
    featureSettings,
  )

  const kycRequiredAboveUSD = !adapterParams?.feature
    ? undefined
    : (featureSettings?.features?.[adapterParams?.feature as keyof typeof featureSettings.features] as { extra_info?: { no_kyc_max_usd_amount?: number } })?.extra_info
        ?.no_kyc_max_usd_amount ?? 0

  // Check if the user has reached the reward limit (e.g., 0 USDT)
  const isReachedTheRewardsLimit = featureSettings.rewards
    ? featureSettings.rewards.amount >= featureSettings.rewards.limit
    : false

  // Check if there is enough budget in the app's wallet to continue distributing rewards
  const rewardsIsEnabled = featureSettings.rewards.enabled

  // Determine if referral bonuses can be displayed in the app
  const shouldDisplayReferralBonuses =
    !isReachedTheRewardsLimit && rewardsIsEnabled

  const timeInSeconds = featureSettings.savings?.regular_transfer_delay || 0

  const savings_regular_transfer_delay_formatted =
    secondsToDurationFormatted(timeInSeconds)

  const reward_limit_formatted = formatFiatCurrency({
    value: featureSettings.rewards?.limit || 0,
    fiatCode: 'USD',
    showDecimals: false,
  })

  const reward_amount_formatted = formatFiatCurrency({
    value: featureSettings.rewards?.amount || 0,
    fiatCode: 'USD',
    showDecimals: false,
  })

  // ============================================
  // Card Provider Selection Logic
  // ============================================

  /**
   * Check if user is allowed to use RAIN card based on country whitelist/blacklist
   */
  const isAllowedToUseRainCard = (): boolean => {
    if (!normalizedCountry) return false

    const rainConfig = featureSettings?.features?.providers?.rain
    if (!rainConfig?.countries) return false

    const rainCountriesItems =
      rainConfig.countries.items?.map((c) => normalizeText(c)) ?? []

    const listType = rainConfig.countries.list_type

    if (listType === 'white_list') {
      return rainCountriesItems.includes(normalizedCountry)
    }

    // black_list: user is allowed if NOT in the list
    return !rainCountriesItems.includes(normalizedCountry)
  }

  /**
   * Check if user is allowed to use Interlace card based on country whitelist/blacklist
   */
  const isAllowedToUseInterlaceCard = (): boolean => {
    if (!normalizedCountry) return false

    const interlaceConfig = featureSettings?.features?.providers?.interlace
    if (!interlaceConfig?.countries) return false

    const interlaceCountriesItems =
      interlaceConfig.countries.items?.map((c) => normalizeText(c)) ?? []

    const listType = interlaceConfig.countries.list_type

    if (listType === 'white_list') {
      return interlaceCountriesItems.includes(normalizedCountry)
    }

    // black_list: user is allowed if NOT in the list
    return !interlaceCountriesItems.includes(normalizedCountry)
  }

  /**
   * Get the appropriate card provider for the user
   * RAIN takes priority over Interlace
   */
  const getCardProvider = (): KemCardProvider | undefined => {
    if (isAllowedToUseRainCard()) {
      return 'rain'
    }

    if (isAllowedToUseInterlaceCard()) {
      return 'interlace'
    }

    return undefined
  }

  // Get reward currency info
  const reward_display_currency_code = featureSettings.rewards?.currency || 'USD'
  const reward_currency_code = featureSettings.rewards?.currency || 'USDT'

  // Feature flags matching mobile
  const is_feature_stocks_enabled = !!featureSettings.features?.stocks
  const is_feature_off_ramp_enabled = !!featureSettings.features?.off_ramp
  const is_feature_kem_card_enabled = !!featureSettings.features?.card

  return {
    ...featureSettings,
    isEmailRequired,
    isKycRequired,
    isPhoneRequired,
    kycRequiredAboveUSD,

    shouldDisplayReferralBonuses,
    isReachedTheRewardsLimit,

    is_feature_stocks_enabled,
    is_feature_off_ramp_enabled,
    is_feature_kem_card_enabled,

    savings_regular_transfer_delay_formatted,
    reward_amount_formatted,
    reward_limit_formatted,
    reward_display_currency_code,
    reward_currency_code,

    cardProvider: getCardProvider(),
  }
}
